import { Link } from "react-router-dom";
import { Heart, Target, Users, Zap, ArrowRight, CheckCircle, Star } from "lucide-react";
import { SEOHead, localBusinessSchema, breadcrumbSchema } from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";

const VALUES = [
  { icon: Zap, title: "Speed Without Compromise", desc: "48-hour delivery doesn't mean cutting corners. It means we're focused, experienced, and passionate about getting your business online fast.", color: "bg-[#FFD600]" },
  { icon: Heart, title: "Genuine Care", desc: "We're a small Aussie team that genuinely cares about your success. When you win, we win. Your website isn't just a project to us — it's your livelihood.", color: "bg-white" },
  { icon: Target, title: "Results Focused", desc: "We build websites that convert visitors into customers. Every design decision is made with your business goals in mind.", color: "bg-[#0055FF] text-white" },
  { icon: Users, title: "No Tech Headaches", desc: "You shouldn't need a computer science degree to get a great website. We handle everything technical so you can focus on running your business.", color: "bg-black text-white" },
];

const STATS = [
  { n: "200+", label: "Websites Built" },
  { n: "48hrs", label: "Average Delivery" },
  { n: "4.9/5", label: "Client Rating" },
  { n: "100%", label: "Aussie Owned" },
];

const TEAM = [
  {
    name: "Tom",
    role: "Founder & Web Developer",
    desc: "The guy behind No Fuss Websites. Tom builds every site from scratch — fast, clean, and built to last. He started this business because he was sick of watching small businesses get overcharged for average websites.",
    img: "https://static.prod-images.emergentagent.com/jobs/e934d8cc-f9a8-4d3b-9340-bf289c297f39/images/6dc49122efc1d720c668d210ee515c25c4d00193d8606fd3d14b563e93f8149a.png"
  },
  {
    name: "James",
    role: "SEO & Marketing",
    desc: "James makes sure your website gets found. He handles SEO strategy, Google rankings, and digital marketing — so the traffic actually shows up after launch.",
    img: "https://static.prod-images.emergentagent.com/jobs/e934d8cc-f9a8-4d3b-9340-bf289c297f39/images/2945e0f64908134139bbb2e915307529531606c3eb19e7b6cff86f31516e869a.png"
  },
  {
    name: "Teagan",
    role: "Sales, Admin & Accounts",
    desc: "Teagan is your first point of contact and keeps everything running smoothly behind the scenes. From onboarding new clients to managing accounts, she makes sure nothing falls through the cracks.",
    img: "https://static.prod-images.emergentagent.com/jobs/e934d8cc-f9a8-4d3b-9340-bf289c297f39/images/dc679821b23b83586da3541c1eccb123758ccc7d6cfdee6385d67f4f55613eef.png"
  },
];

export default function AboutPage() {
  return (
    <main className="pt-16" data-testid="about-page">
      <SEOHead
        title="About Us — Melbourne Web Design Agency"
        description="No Fuss Websites is a Melbourne-based web design agency building affordable, professional websites for Australian small businesses. 200+ websites built, 48-hour delivery, 4.9/5 rating."
        path="/about"
        schema={[localBusinessSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])]}
      />
      <Breadcrumbs items={[{ name: "About Us", path: "/about" }]} />
      {/* Hero */}
      <section className="bg-[#F4F4F5] border-b-4 border-black section-padding">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-black uppercase tracking-widest text-[#0055FF] mb-4">Our Story</p>
              <h1 className="text-5xl md:text-6xl font-black tracking-tight mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
                Aussie Businesses<br />Deserve Better.
              </h1>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We started No Fuss Websites because we were sick of seeing small business owners get ripped off — paying $3,000–$10,000+ for websites that took months to deliver and then fell apart.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                We knew there was a better way. Professional, fast, affordable websites for every Aussie business — with real human support from people who actually care.
              </p>
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="about-cta">
                Work With Us <ArrowRight size={16} />
              </Link>
            </div>
            <div className="border-2 border-black rounded-3xl overflow-hidden" style={{ boxShadow: "8px 8px 0px 0px rgba(0,0,0,1)" }}>
              <img
                src="https://images.pexels.com/photos/1181346/pexels-photo-1181346.jpeg"
                alt="No Fuss Websites Team"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#FFD600] border-b-4 border-black py-12" data-testid="stats-section">
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <div key={i} className="text-center" data-testid={`stat-${i}`}>
                <div className="text-5xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>{s.n}</div>
                <div className="text-sm font-black uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-padding" data-testid="mission-section">
        <div className="container-main max-w-4xl text-center">
          <p className="text-sm font-black uppercase tracking-widest text-[#0055FF] mb-4">Our Mission</p>
          <h2 className="text-4xl md:text-5xl font-black mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>
            "No nonsense. Just great websites for Aussie businesses."
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Every small business in Australia deserves a website that works hard for them — one that looks professional, loads fast, ranks on Google, and converts visitors into customers. And it shouldn't cost a fortune or take months to build.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-[#F4F4F5]" data-testid="values-section">
        <div className="container-main">
          <div className="text-center mb-14">
            <p className="text-sm font-black uppercase tracking-widest text-[#0055FF] mb-3">What We Stand For</p>
            <h2 className="text-4xl md:text-5xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>Our Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {VALUES.map((v, i) => (
              <div key={i} className={`${v.color} border-2 border-black rounded-2xl p-8 flex gap-6 items-start transition-transform hover:-translate-y-1`} style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }} data-testid={`value-${i}`}>
                <div className={`w-12 h-12 border-2 border-black rounded-xl flex items-center justify-center shrink-0 ${v.color === "bg-[#0055FF] text-white" || v.color === "bg-black text-white" ? "bg-[#FFD600] text-black" : "bg-black text-[#FFD600]"}`}>
                  <v.icon size={22} />
                </div>
                <div>
                  <h3 className="text-xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>{v.title}</h3>
                  <p className="text-sm leading-relaxed opacity-85">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding" data-testid="team-section">
        <div className="container-main">
          <div className="text-center mb-14">
            <p className="text-sm font-black uppercase tracking-widest text-[#0055FF] mb-3">The Team</p>
            <h2 className="text-4xl md:text-5xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>Meet Your Aussie Web Team</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TEAM.map((member, i) => (
              <div key={i} className="bg-white border-2 border-black rounded-2xl overflow-hidden transition-transform hover:-translate-y-1" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }} data-testid={`team-${i}`}>
                <div className="w-full h-56 bg-[#F4F4F5] border-b-2 border-black flex items-center justify-center overflow-hidden">
                  <img src={member.img} alt={member.name} className="h-52 w-auto object-contain" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-black mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{member.name}</h3>
                  <p className="text-sm font-bold text-[#0055FF] mb-3">{member.role}</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{member.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="section-padding bg-black text-white" data-testid="location-section">
        <div className="container-main text-center">
          <p className="text-sm font-black uppercase tracking-widest text-[#FFD600] mb-4">Where We Are</p>
          <h2 className="text-4xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Proudly Based in Melbourne, Australia
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            We're a local Melbourne business serving small businesses across Australia. Call, email, or Zoom — we're always here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:0466476264" className="btn-primary inline-flex items-center gap-2" data-testid="about-call-btn">
              Call 0466 476 264
            </a>
            <a href="mailto:hello@nofusswebsites.com.au" className="bg-white text-black font-bold border-2 border-white px-8 py-3 rounded-full hover:bg-[#FFD600] hover:border-[#FFD600] transition-colors">
              hello@nofusswebsites.com.au
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
